/**
 * Scenario Comparison Widget
 * Shows differences between AI plan and original LN plan
 */

import { useState, useMemo } from 'react';
import {
  ArrowUp,
  ArrowDown,
  Minus,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowRightLeft,
  Calendar,
  Clock,
  Lightbulb,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import clsx from 'clsx';
import type {
  ComparisonResponse,
  OperationComparison,
  ComparisonSummary,
} from '../../hooks/useScenarioComparison';
import {
  getDeltaColor,
  calculateComparisonStats,
  groupComparisonsByOrder,
  filterComparisonsByDelta,
} from '../../hooks/useScenarioComparison';

type FilterType = 'all' | 'earlier' | 'later' | 'unchanged' | 'work_center_changed';
type ViewMode = 'list' | 'grouped';

interface ScenarioComparisonWidgetProps {
  data?: ComparisonResponse;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onSelectScenario?: () => void;
  scenarioId?: string;
}

export function ScenarioComparisonWidget({
  data,
  loading,
  error,
  onRefresh,
  onSelectScenario,
  scenarioId,
}: ScenarioComparisonWidgetProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  // Calculate detailed stats
  const stats = useMemo(() => {
    if (!data) return null;
    return calculateComparisonStats(data.comparison);
  }, [data]);

  // Filter and group data
  const filteredComparison = useMemo(() => {
    if (!data) return [];
    return filterComparisonsByDelta(data.comparison, filter);
  }, [data, filter]);

  const groupedComparison = useMemo(() => {
    return groupComparisonsByOrder(filteredComparison);
  }, [filteredComparison]);

  // No scenario selected
  if (!scenarioId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
        <ArrowRightLeft className="w-12 h-12 mb-4 text-gray-300" />
        <p className="text-sm mb-3">No Scenario Selected</p>
        {onSelectScenario && (
          <button
            onClick={onSelectScenario}
            className="px-4 py-2 text-sm bg-[#45bdd6] text-white rounded hover:bg-[#3aa8c0] transition-colors"
          >
            Select Scenario
          </button>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#45bdd6] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading Comparison...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-red-500">
        <ArrowRightLeft className="w-10 h-10 mb-3" />
        <p className="text-sm text-center mb-3">{error.message || 'Error Loading'}</p>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="px-4 py-2 text-sm border border-red-300 rounded hover:bg-red-50"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  // No data
  if (!data || data.comparison.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
        <ArrowRightLeft className="w-10 h-10 mb-3 text-gray-300" />
        <p className="text-sm">No Comparison Data Available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with summary */}
      <div className="px-4 py-3 bg-gray-50 border-b">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-medium text-gray-800">
              {data.scenario_name}
            </h3>
            <p className="text-xs text-gray-500">
              vs. Original ERP Plan
            </p>
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 text-gray-500 hover:text-gray-700"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-2">
          <SummaryCard
            label="Earlier"
            value={data.summary.moved_earlier}
            icon={TrendingUp}
            color="green"
            active={filter === 'earlier'}
            onClick={() => setFilter(filter === 'earlier' ? 'all' : 'earlier')}
          />
          <SummaryCard
            label="Later"
            value={data.summary.moved_later}
            icon={TrendingDown}
            color="red"
            active={filter === 'later'}
            onClick={() => setFilter(filter === 'later' ? 'all' : 'later')}
          />
          <SummaryCard
            label="Unchanged"
            value={data.summary.unchanged}
            icon={Minus}
            color="gray"
            active={filter === 'unchanged'}
            onClick={() => setFilter(filter === 'unchanged' ? 'all' : 'unchanged')}
          />
          <SummaryCard
            label="WC Changed"
            value={data.summary.work_center_changes}
            icon={ArrowRightLeft}
            color="amber"
            active={filter === 'work_center_changed'}
            onClick={() => setFilter(filter === 'work_center_changed' ? 'all' : 'work_center_changed')}
          />
        </div>
      </div>

      {/* Delta Distribution Chart */}
      {stats && (
        <div className="px-4 py-3 bg-white border-b">
          <DeltaDistributionChart comparison={data.comparison} stats={stats} />
          <div className="flex items-center justify-center gap-6 mt-2 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">⌀ Delta:</span>
              <span
                className={clsx(
                  'font-medium',
                  stats.averageDeltaHours < 0 ? 'text-green-600' : stats.averageDeltaHours > 0 ? 'text-red-600' : 'text-gray-600'
                )}
              >
                {stats.averageDeltaHours < 0 ? '' : '+'}
                {stats.averageDeltaHours.toFixed(1)}h
              </span>
            </div>
            {stats.maxEarlierHours > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Max earlier:</span>
                <span className="font-medium text-green-600">
                  -{stats.maxEarlierHours.toFixed(0)}h
                </span>
              </div>
            )}
            {stats.maxLaterHours > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Max later:</span>
                <span className="font-medium text-red-600">
                  +{stats.maxLaterHours.toFixed(0)}h
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View controls */}
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-white">
        <Filter className="w-4 h-4 text-gray-400" />
        <span className="text-xs text-gray-500">
          {filteredComparison.length} of {data.comparison.length} operations
        </span>
        <div className="flex-1" />
        <div className="flex rounded border border-gray-300 overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={clsx(
              'px-2 py-1 text-xs',
              viewMode === 'list' ? 'bg-[#45bdd6] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            )}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('grouped')}
            className={clsx(
              'px-2 py-1 text-xs',
              viewMode === 'grouped' ? 'bg-[#45bdd6] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            )}
          >
            By Order
          </button>
        </div>
      </div>

      {/* Comparison list */}
      <div className="flex-1 overflow-y-auto">
        {viewMode === 'list' ? (
          <div className="divide-y">
            {filteredComparison.map((op, idx) => (
              <ComparisonRow key={`${op.order_number}-${op.operation_sequence}-${idx}`} op={op} />
            ))}
          </div>
        ) : (
          <div className="divide-y">
            {Array.from(groupedComparison.entries()).map(([orderNum, ops]) => (
              <OrderGroup
                key={orderNum}
                orderNumber={orderNum}
                operations={ops}
                isExpanded={expandedOrder === orderNum}
                onToggle={() => setExpandedOrder(expandedOrder === orderNum ? null : orderNum)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Summary card component
interface SummaryCardProps {
  label: string;
  value: number;
  icon: typeof ArrowUp;
  color: 'green' | 'red' | 'gray' | 'amber';
  active: boolean;
  onClick: () => void;
}

function SummaryCard({ label, value, icon: Icon, color, active, onClick }: SummaryCardProps) {
  const colorClasses = {
    green: active ? 'bg-green-100 border-green-300 text-green-700' : 'bg-white border-gray-200 text-green-600',
    red: active ? 'bg-red-100 border-red-300 text-red-700' : 'bg-white border-gray-200 text-red-600',
    gray: active ? 'bg-gray-200 border-gray-400 text-gray-700' : 'bg-white border-gray-200 text-gray-600',
    amber: active ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-amber-600',
  };

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex flex-col items-center p-2 rounded border transition-all hover:shadow-sm',
        colorClasses[color]
      )}
    >
      <Icon className="w-4 h-4 mb-0.5" />
      <span className="text-lg font-semibold">{value}</span>
      <span className="text-[10px] opacity-80">{label}</span>
    </button>
  );
}

// Comparison row component
interface ComparisonRowProps {
  op: OperationComparison;
}

function ComparisonRow({ op }: ComparisonRowProps) {
  const [showReasoning, setShowReasoning] = useState(false);
  const delta = getDeltaColor(op.delta.start_hours);

  return (
    <div className="px-4 py-3 hover:bg-gray-50">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800">
              {op.order_number}
            </span>
            <span className="text-xs text-gray-500">
              Op {op.operation_sequence}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate max-w-xs">
            {op.description}
          </p>
        </div>
        <span className={clsx('px-2 py-0.5 text-xs rounded', delta.bg, delta.text)}>
          {delta.label}
        </span>
      </div>

      {/* Visual Delta Bar */}
      {op.delta.start_hours !== null && op.delta.start_hours !== 0 && (
        <DeltaBar hours={op.delta.start_hours} />
      )}

      {/* Timing comparison */}
      <div className="grid grid-cols-2 gap-4 text-xs mt-2">
        <div>
          <div className="text-gray-500 mb-1 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-gray-300" />
            Original (ERP)
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400" />
            <span>
              {op.original.start
                ? new Date(op.original.start).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'}
            </span>
          </div>
          {op.original.work_center && (
            <div className="text-gray-500 mt-0.5">WC: {op.original.work_center}</div>
          )}
        </div>
        <div>
          <div className="text-gray-500 mb-1 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-[#45bdd6]" />
            AI Plan
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-[#45bdd6]" />
            <span className="text-[#45bdd6]">
              {new Date(op.planned.start).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          {op.planned.work_center && (
            <div
              className={clsx(
                'mt-0.5',
                op.delta.work_center_changed ? 'text-amber-600 font-medium' : 'text-gray-500'
              )}
            >
              WC: {op.planned.work_center}
              {op.delta.work_center_changed && (
                <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0 text-[10px] bg-amber-100 text-amber-700 rounded">
                  <ArrowRightLeft className="w-2.5 h-2.5" />
                  changed
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Reasoning */}
      {op.ai_reasoning && (
        <div className="mt-2">
          <button
            onClick={() => setShowReasoning(!showReasoning)}
            className="flex items-center gap-1 text-xs text-[#45bdd6] hover:underline"
          >
            <Lightbulb className="w-3 h-3" />
            {showReasoning ? 'Hide Reasoning' : 'Show AI Reasoning'}
          </button>
          {showReasoning && (
            <div className="mt-1 p-2 bg-blue-50 rounded text-xs text-gray-700">
              {op.ai_reasoning}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Order group component
interface OrderGroupProps {
  orderNumber: string;
  operations: OperationComparison[];
  isExpanded: boolean;
  onToggle: () => void;
}

function OrderGroup({ orderNumber, operations, isExpanded, onToggle }: OrderGroupProps) {
  // Calculate order-level stats
  const orderStats = useMemo(() => {
    let totalDelta = 0;
    let earlierCount = 0;
    let laterCount = 0;

    for (const op of operations) {
      if (op.delta.start_hours !== null) {
        totalDelta += op.delta.start_hours;
        if (op.delta.start_hours < 0) earlierCount++;
        if (op.delta.start_hours > 0) laterCount++;
      }
    }

    return {
      totalDelta,
      earlierCount,
      laterCount,
      avgDelta: operations.length > 0 ? totalDelta / operations.length : 0,
    };
  }, [operations]);

  return (
    <div>
      {/* Order header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-800">{orderNumber}</span>
          <span className="text-xs text-gray-500">
            {operations.length} operations
          </span>
        </div>

        <div className="flex items-center gap-3">
          {orderStats.earlierCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <ArrowUp className="w-3 h-3" />
              {orderStats.earlierCount}
            </span>
          )}
          {orderStats.laterCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <ArrowDown className="w-3 h-3" />
              {orderStats.laterCount}
            </span>
          )}
          <span
            className={clsx(
              'text-xs font-medium',
              orderStats.avgDelta < 0
                ? 'text-green-600'
                : orderStats.avgDelta > 0
                ? 'text-red-600'
                : 'text-gray-500'
            )}
          >
            ⌀ {orderStats.avgDelta < 0 ? '' : '+'}
            {orderStats.avgDelta.toFixed(1)}h
          </span>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded operations */}
      {isExpanded && (
        <div className="border-t bg-gray-50">
          {operations.map((op, idx) => (
            <ComparisonRow
              key={`${op.order_number}-${op.operation_sequence}-${idx}`}
              op={op}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Delta Bar: visual magnitude indicator ──────────────────

function DeltaBar({ hours }: { hours: number }) {
  // Max bar width at 48h delta
  const maxHours = 48;
  const absHours = Math.min(Math.abs(hours), maxHours);
  const widthPercent = (absHours / maxHours) * 50; // 50% = half the bar (center-aligned)

  const isEarlier = hours < 0;

  return (
    <div className="mt-2 mb-1">
      <div className="relative h-3 bg-gray-50 rounded-full overflow-hidden border border-gray-100">
        {/* Center line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 z-10" />
        {/* Delta bar */}
        <div
          className={clsx(
            'absolute top-0 bottom-0 transition-all duration-300',
            isEarlier ? 'bg-green-400/60 rounded-l-full' : 'bg-red-400/60 rounded-r-full'
          )}
          style={{
            ...(isEarlier
              ? { right: '50%', width: `${widthPercent}%` }
              : { left: '50%', width: `${widthPercent}%` }),
          }}
        />
      </div>
      <div className="flex justify-between mt-0.5 text-[10px] text-gray-400">
        <span>Earlier</span>
        <span className={clsx('font-medium', isEarlier ? 'text-green-600' : 'text-red-600')}>
          {isEarlier ? '' : '+'}{hours.toFixed(1)}h
        </span>
        <span>Later</span>
      </div>
    </div>
  );
}

// ─── Delta Distribution Chart ──────────────────────────────

function DeltaDistributionChart({
  comparison,
  stats,
}: {
  comparison: OperationComparison[];
  stats: ReturnType<typeof calculateComparisonStats>;
}) {
  // Build histogram buckets: -48h to +48h in 8h steps
  const bucketSize = 8;
  const bucketRange = 48;
  const numBuckets = (bucketRange * 2) / bucketSize; // 12 buckets
  const buckets = new Array(numBuckets).fill(0);

  for (const op of comparison) {
    if (op.delta.start_hours === null) continue;
    const clamped = Math.max(-bucketRange, Math.min(bucketRange, op.delta.start_hours));
    const idx = Math.min(
      Math.floor((clamped + bucketRange) / bucketSize),
      numBuckets - 1
    );
    buckets[idx]++;
  }

  const maxBucket = Math.max(...buckets, 1);

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
          Change Distribution
        </span>
        <span className="text-[10px] text-gray-400">
          ({stats.totalOperations} operations)
        </span>
      </div>
      <div className="flex items-end gap-px h-10">
        {buckets.map((count, i) => {
          const isCenter = i === numBuckets / 2 - 1 || i === numBuckets / 2;
          const isLeft = i < numBuckets / 2;
          const heightPercent = (count / maxBucket) * 100;
          const bucketStart = -bucketRange + i * bucketSize;
          const bucketEnd = bucketStart + bucketSize;

          return (
            <div
              key={i}
              className="flex-1 relative group"
              title={`${bucketStart}h to ${bucketEnd}h: ${count} ops`}
            >
              <div
                className={clsx(
                  'w-full rounded-t-sm transition-all',
                  count === 0 ? 'bg-gray-100' :
                  isLeft ? 'bg-green-400/70 hover:bg-green-500/80' :
                  'bg-red-400/70 hover:bg-red-500/80'
                )}
                style={{ height: count === 0 ? '2px' : `${Math.max(heightPercent, 8)}%` }}
              />
              {/* Tooltip on hover */}
              {count > 0 && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 bg-gray-800 text-white text-[9px] rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-20 transition-opacity">
                  {count} op{count !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
        <span>-{bucketRange}h</span>
        <span className="text-gray-500 font-medium">0</span>
        <span>+{bucketRange}h</span>
      </div>
    </div>
  );
}

export default ScenarioComparisonWidget;
